import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { DollarSign, Plus, Pencil } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import type { NurseryPriceList } from '../../types/nursery';

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-neutral-100 text-neutral-600',
  active: 'bg-green-50 text-green-700 border border-green-100',
  archived: 'bg-amber-50 text-amber-700 border border-amber-100',
};

function PriceListList() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const { hasAccess: canManage } = useModuleLicense('accounts', ['user', 'admin']);
  const [lists, setLists] = useState<NurseryPriceList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await nurseryService.getPriceLists();
        if (res.success) setLists(res.data || []);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load price lists');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <>
      <PageHeader
        title="Price Lists"
        subtitle="Versioned, tier-scoped pricing with volume breaks"
        icon={<DollarSign className="h-6 w-6 text-white" />}
        actionButtons={
          canManage && (
            <Button variant="primary" size="sm" onClick={() => navigate('/accounts/price-lists/new')}>
              <Plus className="h-4 w-4" /> New Price List
            </Button>
          )
        }
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">{error}</div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-neutral-100 rounded-lg animate-pulse" />)}
          </div>
        ) : lists.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">No price lists yet</p>
            <p className="text-sm mt-1">Create a price list to drive default order pricing.</p>
            {canManage && (
              <Button variant="primary" className="mt-4" onClick={() => navigate('/accounts/price-lists/new')}>
                <Plus className="h-4 w-4" /> New Price List
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
            {lists.map(pl => (
              <div key={pl.id} className="flex items-start gap-3 px-4 py-3 hover:bg-neutral-50">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-medium text-neutral-800 break-words min-w-0">{pl.name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${STATUS_STYLES[pl.status] || ''}`}>{pl.status}</span>
                    {pl.customer_name && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-teal-50 text-teal-800 border border-teal-100 break-words min-w-0">
                        Contract list: {pl.customer_name}
                      </span>
                    )}
                    {pl.tier_name && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 break-words min-w-0">{pl.tier_name}</span>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500">
                    {formatLocalDateInTz(pl.effective_from, orgTz)}
                    {pl.effective_to ? ` – ${formatLocalDateInTz(pl.effective_to, orgTz)}` : ' – ongoing'}
                    {` · ${pl.item_count ?? 0} prices`}
                  </p>
                </div>
                {canManage && (
                  <button
                    onClick={() => navigate(`/accounts/price-lists/${pl.id}/edit`)}
                    className="text-neutral-400 hover:text-primary-600 p-1"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default PriceListList;
